import { Card } from '@/components/ui/card';
import { useClients } from '@/hooks/useClients';
import { AdminSidebar } from '@/components/AdminSidebar';
import { DashboardHeader } from '@/components/DashboardHeader';
import { DashboardFooter } from '@/components/DashboardFooter';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useEffect, useState } from 'react';
import { SearchInput } from './SearchInput';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ClientTableRow } from './clients/ClientTableRow';
import { ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

type SortField = 'name' | 'email' | 'status' | 'source' | 'last_contact';
type SortOrder = 'asc' | 'desc';

export const AllClientsView = () => {
  const { clients, isLoading, handleDeleteClient } = useClients();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const sortedClients = [...filteredClients].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    const comparison = aValue.toString().localeCompare(bValue.toString());
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const content = isLoading ? (
    <div className="flex items-center justify-center flex-1 h-[calc(100vh-8rem)]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  ) : (
    <main className="flex-1 p-4 md:p-6 bg-background overflow-y-auto mt-16">
      <Card className="w-full max-w-7xl mx-auto p-4 md:p-6 bg-[#222222] border-white/10">
        <div className="space-y-8">
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Client Management</h1>
              <p className="text-muted-foreground">
                Manage and track all client information in one place
              </p>
            </div>
            
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-white">
                  All Clients
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({sortedClients.length})
                  </span>
                </h2>
              </div>
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search clients by name or email..."
              />
            </div>
          </div>

          <div className="rounded-md border border-white/10">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-white/5">
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('name')}
                      className="text-white hover:text-white/90"
                    >
                      Name
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('email')}
                      className="text-white hover:text-white/90"
                    >
                      Email
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('status')}
                      className="text-white hover:text-white/90"
                    >
                      Status
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('source')}
                      className="text-white hover:text-white/90"
                    >
                      Source
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('last_contact')}
                      className="text-white hover:text-white/90"
                    >
                      Last Contact
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      <p>No clients found</p>
                      <p className="text-sm">Add new clients to see them listed here</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedClients.map((client) => (
                    <ClientTableRow 
                      key={client.id} 
                      client={client}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>
    </main>
  );

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-[#0F1219]">
        <AdminSidebar />
        <div className="flex-1 flex flex-col">
          <DashboardHeader />
          {content}
          <DashboardFooter />
        </div>
      </div>
    </SidebarProvider>
  );
};